import { useEffect, useRef, useState } from "react";
import { Phone, Mail, MapPin, Instagram, Send, Clock, MessageCircle, ImagePlus, X, Check } from "lucide-react";
import { SectionHead, Reveal } from "./Reveal";
import { useContent } from "@/context/ContentContext";
import { API, buildServiceOptions, buildTelLink, buildWhatsLink, activeAddons, VEHICLE_OPTIONS, formatEur } from "@/lib/site";

const inputCls =
  "w-full rounded-lg border border-zinc-700 bg-zinc-900/60 px-4 h-11 text-sm text-zinc-100 placeholder:text-zinc-400 outline-none focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/20 transition-all";

function TikTokIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 1 1-2.31-2.83v-3.5a6.37 6.37 0 1 0 5.76 6.33V8.69a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.12z" />
    </svg>
  );
}

export default function Booking({ preset }) {
  const c = useContent();
  const t = c.texts;
  const S = c.settings;
  const tel = buildTelLink(S);
  const whats = buildWhatsLink(S);
  const addonsList = activeAddons(c);
  const baseOptions = buildServiceOptions(c);
  const area = c.service_area || {};
  const city = (area.origin || "").replace(/^\d{5}\s*/, "").trim();
  const areaTextParts = [];
  if (city && !area.travel_fee) areaTextParts.push(`Im direkten Raum ${city} ist die Anfahrt aktuell kostenlos.`);
  if (area.travel_fee) areaTextParts.push(`Anfahrtskosten: ${area.travel_fee}.`);
  if (area.max_radius_km) areaTextParts.push(`Unser reguläres Einsatzgebiet umfasst einen Umkreis von bis zu ${area.max_radius_km} km.`);
  areaTextParts.push("Bei längeren Anfahrten stimmen wir mögliche Anfahrtskosten vor deinem Termin transparent mit dir ab.");
  const areaChips = [];
  if (city && !area.travel_fee) areaChips.push(`Kostenlose Anfahrt im Raum ${city}`);
  if (area.free_radius_km != null) areaChips.push(`Kostenfreie Anfahrt bis ${area.free_radius_km} km`);
  if (area.max_radius_km) areaChips.push(`Mobiler Service bis ${area.max_radius_km} km Umkreis`);
  areaChips.push("Weitere Entfernungen auf Anfrage");
  const hasAreaInfo = city || (area.places && area.places.length) || area.note;

  const [form, setForm] = useState(() => ({
    name: "",
    contact_method: "phone",
    phone: "",
    email: "",
    zip_city: "",
    vehicle: VEHICLE_OPTIONS[0],
    package: "",
    addon_ids: [],
    estimated_price: "",
    desired_date: "",
    message: "",
    consent: false,
  }));
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [presetApplied, setPresetApplied] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!preset) return;
    setPresetApplied(true);
    setForm((f) => ({
      ...f,
      package: preset.service || f.package,
      vehicle: preset.vehicle && VEHICLE_OPTIONS.includes(preset.vehicle) ? preset.vehicle : f.vehicle,
      addon_ids: Array.isArray(preset.addonIds) ? preset.addonIds.filter((id) => addonsList.some((a) => a.id === id)) : f.addon_ids,
      estimated_price: preset.estimated || f.estimated_price,
      message: preset.notes || f.message,
    }));
  }, [preset]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const toggleAddon = (id) =>
    setForm((f) => ({
      ...f,
      addon_ids: f.addon_ids.includes(id) ? f.addon_ids.filter((x) => x !== id) : [...f.addon_ids, id],
    }));

  const addPhotos = (files) => {
    for (const f of files) {
      if (photos.length >= 4) {
        alert("Maximal 4 Fotos pro Anfrage.");
        break;
      }
      if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(f.type)) {
        alert(`${f.name}: Nur JPG-, PNG-, WebP- oder GIF-Bilder.`);
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        alert(`${f.name}: Bild ist zu groß (max. 10 MB).`);
        continue;
      }
      setPhotos((p) => [...p, f]);
    }
  };

  const packageOptions = form.package && !baseOptions.includes(form.package) ? [...baseOptions, form.package] : baseOptions;

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const photoPaths = [];
      for (const f of photos) {
        const fd = new FormData();
        fd.append("file", f);
        const up = await fetch(`${API}/inquiry-upload`, { method: "POST", body: fd });
        const upData = await up.json().catch(() => ({}));
        if (!up.ok) throw new Error(upData.detail || "Foto-Upload fehlgeschlagen.");
        photoPaths.push(upData.path);
      }
      const res = await fetch(`${API}/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, phone: form.phone || null, email: form.email || null, photos: photoPaths }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Etwas ist schiefgelaufen.");
      setDone(true);
    } catch (err) {
      setDone({ error: String(err.message || err) });
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setDone(false);
    setPhotos([]);
    setPresetApplied(false);
    setForm({
      name: "",
      contact_method: "phone",
      phone: "",
      email: "",
      zip_city: "",
      vehicle: VEHICLE_OPTIONS[0],
      package: "",
      addon_ids: [],
      estimated_price: "",
      desired_date: "",
      message: "",
      consent: false,
    });
  };

  const selectedAddons = addonsList.filter((a) => form.addon_ids.includes(a.id));
  const addonLabel = (a) => {
    const price = a.prices_per_class && a.prices_per_class[a.id] != null ? a.price : a.price;
    return `${a.name} (+${formatEur(price)}${a.per_seat ? " / Sitz" : ""})`;
  };

  return (
    <section id="termin" data-testid="booking-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="glow-blue pointer-events-none absolute -bottom-52 left-0 h-[500px] w-[500px]" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <Reveal>
          <SectionHead eyebrow={t.booking_eyebrow} title={t.booking_title} sub={t.booking_sub} />

          {hasAreaInfo ? (
            <div className="mt-8 rounded-2xl border border-[#27272A] bg-[#121215] p-6">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-zinc-400 mb-3">
                <MapPin className="h-4 w-4 text-blue-400" /> Einsatzgebiet &amp; Anfahrt
              </p>
              <div className="flex flex-col gap-2 text-sm text-zinc-200">
                {area.origin ? <p>Ausgangspunkt: <span className="text-zinc-400">{area.origin}</span></p> : null}
                {area.places?.length ? <p>Bereiche: <span className="text-zinc-400">{area.places.join(", ")}</span></p> : null}
                {area.free_radius_km != null ? <p>Anfahrt frei bis <span className="text-zinc-400">{area.free_radius_km} km</span></p> : null}
                {area.travel_fee ? <p>Anfahrt: <span className="text-zinc-400">{area.travel_fee}</span></p> : null}
                {area.max_radius_km != null ? <p>Maximal: <span className="text-zinc-400">{area.max_radius_km} km</span></p> : null}
                {area.note ? <p className="text-zinc-400 leading-relaxed">{area.note}</p> : null}
              </div>
            </div>
          ) : null}

          <div className="mt-8 flex flex-col gap-5">
            {tel ? (
              <a
                data-testid="booking-phone-link"
                href={tel}
                className="group flex items-center gap-4 rounded-xl border border-[#27272A] bg-[#121215] p-5 hover:border-blue-500/40 transition-colors"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-500/10">
                  <Phone className="h-5 w-5 text-blue-400" />
                </span>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">Telefon</p>
                  <p className="font-semibold text-zinc-100">{S.phone_display}</p>
                </div>
              </a>
            ) : null}
            {whats ? (
              <a
                data-testid="booking-whatsapp-link"
                href={whats}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 rounded-xl border border-[#27272A] bg-[#121215] p-5 hover:border-blue-500/40 transition-colors"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-500/10">
                  <MessageCircle className="h-5 w-5 text-blue-400" />
                </span>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">WhatsApp</p>
                  <p className="font-semibold text-zinc-100">Direkt schreiben</p>
                </div>
              </a>
            ) : null}
            <a
              data-testid="booking-email-link"
              href={`mailto:${S.email}`}
              className="group flex items-center gap-4 rounded-xl border border-[#27272A] bg-[#121215] p-5 hover:border-blue-500/40 transition-colors"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-500/10">
                <Mail className="h-5 w-5 text-blue-400" />
              </span>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">E-Mail</p>
                <p className="font-semibold text-zinc-100 break-all">{S.email}</p>
              </div>
            </a>
            <div className="flex items-center gap-4 rounded-xl border border-[#27272A] bg-[#121215] p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-500/10">
                <MapPin className="h-5 w-5 text-blue-400" />
              </span>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">Gebiet</p>
                <p className="font-semibold text-zinc-100">{S.area}</p>
              </div>
            </div>
            {S.hours?.map((h) => (
              <div key={h.label} className="flex items-center gap-4 rounded-xl border border-[#27272A] bg-[#121215] p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-500/10">
                  <Clock className="h-5 w-5 text-blue-400" />
                </span>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">{h.label}</p>
                  <p className="font-semibold text-zinc-100">{h.value}</p>
                </div>
              </div>
            ))}
            <div className="flex gap-3">
              {S.instagram ? (
                <a
                  data-testid="booking-instagram-link"
                  href={S.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-full border border-[#27272A] px-5 h-11 text-sm text-zinc-300 hover:border-blue-500/50 hover:text-blue-400 transition-colors"
                >
                  <Instagram className="h-4 w-4" /> Instagram
                </a>
              ) : null}
              {S.tiktok ? (
                <a
                  data-testid="booking-tiktok-link"
                  href={S.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-full border border-[#27272A] px-5 h-11 text-sm text-zinc-300 hover:border-blue-500/50 hover:text-blue-400 transition-colors"
                >
                  <TikTokIcon className="h-4 w-4" /> TikTok
                </a>
              ) : null}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-6 sm:p-8">
            {done === true ? (
              <div data-testid="booking-success-panel" className="flex min-h-[480px] flex-col items-center justify-center text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/10 border border-blue-500/40">
                  <Send className="h-6 w-6 text-blue-400" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-semibold text-zinc-100">Anfrage gesendet!</h3>
                <p className="mt-3 max-w-sm text-sm text-zinc-300 leading-relaxed">
                  Danke für dein Vertrauen. Ich melde mich so schnell wie möglich bei dir mit einer
                  Rückmeldung zu Termin und Preis.
                </p>
                <button
                  data-testid="booking-reset-button"
                  onClick={reset}
                  className="mt-8 h-11 rounded-full border border-zinc-600 px-6 text-sm font-semibold text-zinc-200 hover:border-blue-500/60 hover:text-blue-400 transition-colors"
                >
                  Weitere Anfrage senden
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col gap-4">
                {done && done.error ? (
                  <p data-testid="booking-error-alert" className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
                    {done.error}
                  </p>
                ) : null}
                {(presetApplied && (form.estimated_price || form.package)) ? (
                  <p data-testid="booking-preset-chip" className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-2.5 font-mono text-xs text-blue-400">
                    Vorausgewählt: {form.estimated_price || form.package} · Extras lassen sich unten ändern
                  </p>
                ) : null}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    data-testid="booking-name-input"
                    className={inputCls}
                    placeholder="Name *"
                    value={form.name}
                    onChange={set("name")}
                    required
                    minLength={2}
                    maxLength={80}
                    autoComplete="name"
                  />
                  <input
                    data-testid="booking-zip-input"
                    className={inputCls}
                    placeholder="PLZ / Ort *"
                    value={form.zip_city}
                    onChange={set("zip_city")}
                    required
                    minLength={3}
                    maxLength={120}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-zinc-400">
                    Bevorzugter Kontaktweg *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: "phone", label: "Telefon" },
                      { id: "email", label: "E-Mail" },
                    ].map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        data-testid={`booking-contact-${m.id}`}
                        onClick={() => setForm((f) => ({ ...f, contact_method: m.id }))}
                        className={`h-11 rounded-lg border text-sm font-semibold transition-colors ${
                          form.contact_method === m.id
                            ? "border-blue-500 bg-blue-500/10 text-blue-400"
                            : "border-zinc-700 bg-zinc-900/60 text-zinc-300"
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>

                {form.contact_method === "email" ? (
                  <input
                    data-testid="booking-email-input"
                    className={inputCls}
                    type="email"
                    placeholder="E-Mail *"
                    value={form.email || ""}
                    onChange={set("email")}
                    required
                    maxLength={200}
                    autoComplete="email"
                  />
                ) : (
                  <input
                    data-testid="booking-phone-input"
                    className={inputCls}
                    type="tel"
                    placeholder="Telefonnummer *"
                    value={form.phone || ""}
                    onChange={set("phone")}
                    required
                    minLength={5}
                    maxLength={40}
                    autoComplete="tel"
                  />
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-zinc-400">
                      Fahrzeug *
                    </label>
                    <select data-testid="booking-vehicle-select" className={inputCls} value={form.vehicle} onChange={set("vehicle")}>
                      {VEHICLE_OPTIONS.map((v) => (
                        <option key={v} value={v} className="bg-zinc-900">
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-zinc-400">
                      Gewünschte Leistung *
                    </label>
                    <select data-testid="booking-service-select" className={inputCls} value={form.package} onChange={set("package")} required>
                      <option value="" className="bg-zinc-900" disabled>
                        Bitte wählen
                      </option>
                      {packageOptions.map((s) => (
                        <option key={s} value={s} className="bg-zinc-900">
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {addonsList.length > 0 && (
                  <div>
                    <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-zinc-400">
                      Zusatzleistungen (optional)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {addonsList.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          data-testid="booking-addon-chip"
                          onClick={() => toggleAddon(a.id)}
                          className={`rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${
                            form.addon_ids.includes(a.id)
                              ? "border-blue-500 bg-blue-500/10 text-blue-400"
                              : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                          }`}
                        >
                          {addonLabel(a)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-zinc-400">
                    Wunschtermin (optional)
                  </label>
                  <input
                    data-testid="booking-date-picker"
                    className={inputCls}
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    value={form.desired_date}
                    onChange={set("desired_date")}
                  />
                </div>

                <textarea
                  data-testid="booking-notes-textarea"
                  className={`${inputCls} min-h-[96px] py-3 resize-y`}
                  placeholder="Nachricht (Fahrzeugmodell, Besonderheiten, Fehlercodes …)"
                  value={form.message}
                  onChange={set("message")}
                  maxLength={2000}
                />

                <div>
                  <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-zinc-400">
                    Fahrzeugfotos (optional, max. 4)
                  </label>
                  <input
                    ref={fileRef}
                    data-testid="booking-photos-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      addPhotos(Array.from(e.target.files || []));
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    data-testid="booking-photos-button"
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex h-11 items-center gap-2 rounded-full border border-zinc-600 px-5 text-sm font-semibold text-zinc-200 hover:border-blue-500/60 hover:text-blue-400 transition-colors"
                  >
                    <ImagePlus className="h-4 w-4" />
                    Fotos auswählen
                  </button>
                  <p className="mt-2 text-xs text-zinc-400 leading-relaxed">
                    Zeig mir gern den Zustand: Innenraum, Sitze, Flecken, Tierhaare oder Problemstellen.
                  </p>
                  {photos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="booking-photos-preview">
                      {photos.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-2 rounded-full border border-zinc-700 bg-zinc-900/60 pl-3 pr-1.5 py-1 text-xs text-zinc-300">
                          <span className="max-w-[140px] truncate">{f.name}</span>
                          <button
                            type="button"
                            onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))}
                            className="flex h-5 w-5 items-center justify-center rounded-full text-zinc-400 hover:text-rose-400"
                            aria-label="Foto entfernen"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <label className="flex items-start gap-3 text-xs text-zinc-300 leading-relaxed">
                  <input
                    data-testid="booking-consent-checkbox"
                    type="checkbox"
                    checked={form.consent}
                    onChange={set("consent")}
                    required
                    className="mt-0.5 h-4 w-4 shrink-0 accent-blue-500"
                  />
                  <span>
                    Ich bin damit einverstanden, dass meine Angaben und Fotos zur Bearbeitung meiner
                    Anfrage gespeichert und verarbeitet werden. *
                  </span>
                </label>
                <button
                  data-testid="booking-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-2 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-blue-600 text-sm font-semibold text-white hover:bg-blue-500 transition-colors disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      {photos.length ? "Fotos & Anfrage werden gesendet …" : "Wird gesendet …"}
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Unverbindlich anfragen
                    </>
                  )}
                </button>
                <p className="text-xs text-zinc-400">
                  Unverbindliche Anfrage – sie geht per E-Mail an {S.email} und wird im Adminbereich
                  bearbeitet.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
